import { SCREEN_NAME, CLICK_ACTIONS, IMPRESSION_ACTIONS } from "../constants";
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";
import { PlanDetails, SelectiveCouponVariantXP } from "@swiggy-private/cart-types";
import { getCouponType } from "../helper";
import { CouponsAnalyticsExtraContext, CouponsAnalyticsExtraParams } from "@cart-client/interfaces/Coupon";
export const clickBillDetailAddTip = (clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_BILL_DETAILS_ADD_TIP,
    });
};

export const impressionBillDetailSummary = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    {
        groupCartId,
        cartId,
        addressId,
        tag,
    }: {
        groupCartId?: string;
        cartId?: number;
        addressId?: string;
        tag?: string;
    },
): void => {
    const extraParams: Record<string, unknown> = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        grp_cart_id: groupCartId,
        cartId,
        addressId,
        tag,
    };

    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CHECKOUT_BILL_SUMMARY,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        extraParams: JSON.stringify(extraParams),
    });
};

export const clickApplyCoupon = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    {
        xpSelectiveCouponVariant,
        groupCartId,
        isPaymentCouponWidget,
        extraContext = {},
        extraParams = {},
    }: {
        xpSelectiveCouponVariant: SelectiveCouponVariantXP;
        groupCartId?: string;
        isPaymentCouponWidget?: boolean;
        extraContext?: CouponsAnalyticsExtraContext;
        extraParams?: CouponsAnalyticsExtraParams;
    },
): void => {
    let isBestCoupon = null;
    if (xpSelectiveCouponVariant === SelectiveCouponVariantXP.DISABLE_CTA) {
        isBestCoupon = true;
    }
    if (xpSelectiveCouponVariant === SelectiveCouponVariantXP.DISABLE_DESCRIPTION) {
        isBestCoupon = false;
    }

    const context: Record<string, unknown> = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        coupon_type: getCouponType(isPaymentCouponWidget),
        ...(extraContext ?? {}),
    };

    if (isBestCoupon !== null) {
        context.bestCoupon = isBestCoupon;
    }

    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_APPLY_COUPON,
        context: JSON.stringify(context),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        extraParams: JSON.stringify({ grp_cart_id: groupCartId, ...(extraParams ?? {}) }),
    });
};

export const clickCouponSavingsPopup = (
    context: string,
    clickEvent: AnalyticsContextValue["clickEvent"],
    groupCartId?: string,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_SAVINGS_POPUP,
        context,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        extraParams: JSON.stringify({ grp_cart_id: groupCartId }),
    });
};

export const clickCouponSavingsPopupWithPOR = (
    text: string,
    clickEvent: AnalyticsContextValue["clickEvent"],
    groupCartId?: string,
) => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_SAVINGS_POPUP,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: text,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        extraParams: JSON.stringify({ grp_cart_id: groupCartId }),
    });
};

export const clickCouponMaxSavingsPopup = (
    text: string,
    clickEvent: AnalyticsContextValue["clickEvent"],
    groupCartId?: string,
) => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_MAX_SAVINGS_POPUP,
        context: text,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        extraParams: JSON.stringify({ grp_cart_id: groupCartId }),
    });
};

export const impressionDeliveryFeeLineItem = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    label: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_DELIVERY_FEE_LINE_ITEM,
        label,
    });
};

export const impressionOfferCashRewardLineItem = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    label: string,
    cartId?: number,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_OFFER_CASH_REWARD_LINE_ITEM,
        label,
        context: JSON.stringify({ cartId }),
    });
};

export const impressionTaxesAndChargesLineItem = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    label: string,
    cartId?: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_TAXES_AND_CHARGES_POPUP,
        label,
        context: JSON.stringify({ cartId }),
    });
};

export const clickDeliveryFeeLineItem = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    ctaText: string,
    planDetails?: PlanDetails,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_DELIVERY_FEE_LINE_ITEM,
        context: JSON.stringify({ ctaText, ...(planDetails ?? {}) }),
    });
};

export const impressionDeliveryFeeUnCollapsedLineItem = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    label: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_DELIVERY_FEE_UN_COLLAPSED_LINE_ITEM,
        label,
    });
};

export const impressionBillViewChevron = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId?: string,
    context?: Record<string, unknown>,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_BILL_VIEW_CHEVRON,
        context: JSON.stringify({ cartId, ...context }),
    });
};

export const impressionCollapsibleBillUpsell = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId: string,
    upsellMessage: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_BILL_WIDGET_UPSELL_DATA,
        context: JSON.stringify({ cartId, upsellMessage }),
    });
};

export const clickBillViewChevron = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartId?: string,
    context?: Record<string, unknown>,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.CLICK_BILL_VIEW_CHEVRON,
        context: JSON.stringify({ cartId, ...context }),
    });
};

export const impressionSavingsStamp = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId: string,
    restaurantId: string,
    context: Record<string, unknown>,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_SAVINGS_STAMP,
        context: JSON.stringify({ cartId, restaurantId, ...context }),
    });
};
